import PropTypes from "prop-types";
import { Dropdown } from "primereact/dropdown";

import Label from "../Label";

/**
 * A select dropdown field with an optional label, helper text, and error message.
 *
 * @param {object} props
 * @param {string} props.id - Unique identifier for the input; used to associate the label and ARIA descriptions.
 * @param {string} [props.label] - Text displayed as the input label.
 * @param {string} [props.name] - Forwarded in the normalized onChange event as `target.name`.
 * @param {*} [props.value] - The current selected value; must be provided for controlled usage.
 * @param {Array<object>} [props.options] - Array of option objects to display in the dropdown.
 * @param {string} [props.optionLabel] - Field name on each option object used as the display text.
 * @param {string} [props.optionValue] - Field name on each option object used as the emitted value.
 * @param {boolean} [props.isRequired=false] - When true, renders a required indicator on the label and sets `aria-required` on the input.
 * @param {string} [props.placeholder] - Placeholder text shown when no option is selected.
 * @param {function} [props.onChange] - Called with a DOM-compatible event `{ target: { name, value } }` when the selection changes.
 * @param {string} [props.error] - Error message displayed below the input; also marks the input as invalid.
 * @param {string} [props.helperText] - Hint text shown below the input when there is no error.
 * @param {boolean} [props.disabled=false] - When true, the dropdown is non-interactive.
 * @param {string} [props.className] - Additional CSS classes applied to the input group wrapper.
 * @param {boolean} [props.isLoading=false] - When true, shows a loading spinner in the dropdown icon and disables interaction.
 * @param {object} [props.virtualScrollerOptions] - Additional options passed to the Dropdown's virtual scroller for performance with large datasets.
 *
 * @returns {JSX.Element}
 */
const InputSelect = ({
  id,
  label,
  name,
  value,
  options,
  optionLabel,
  optionValue,
  isRequired,
  onChange,
  error,
  placeholder,
  helperText,
  disabled,
  className,
  isLoading,
  virtualScrollerOptions,
}) => {
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const describedBy =
    [error ? errorId : null, !error && helperText ? helperId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const handleOnChange = (e) => {
    onChange({ target: { name, value: e.value } });
  };

  return (
    <section aria-labelledby={labelId} className="flex flex-col gap-1">
      <div id={labelId}>
        <Label htmlFor={id} text={label} isRequired={!!isRequired} />
      </div>
      <div className={`p-inputgroup flex-1 ${className ?? ""}`.trim()}>
        <Dropdown
          inputId={id}
          aria-labelledby={labelId}
          aria-required={isRequired || undefined}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          dropdownIcon={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-angle-down'}
          value={value}
          options={options ?? []}
          optionLabel={optionLabel}
          optionValue={optionValue}
          placeholder={placeholder}
          onChange={handleOnChange}
          disabled={!!disabled}
          invalid={(!!error).toString()}
          virtualScrollerOptions={virtualScrollerOptions}
          className={`!h-5 w-full${error ? " p-invalid" : ""}`}
          emptyMessage={isLoading ? "Cargando..." : "No se encontraron opciones"}
          emptyFilterMessage={isLoading ? "Cargando..." : "No se encontraron opciones"}
        />
      </div>
      {!!helperText && !error && (
        <small id={helperId} className="-mt-1 text-gray-500">
          {helperText}
        </small>
      )}
      {!!error && (
        <small id={errorId} role="alert" className="-mt-1 text-red-500">
          {error}
        </small>
      )}
    </section>
  );
};

InputSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.any,
  options: PropTypes.arrayOf(PropTypes.object),
  optionLabel: PropTypes.string,
  optionValue: PropTypes.string,
  isRequired: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  virtualScrollerOptions: PropTypes.shape({
    itemSize: PropTypes.number,
  }),
};

export default InputSelect;
